import React from 'react';

import { useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Button } from 'src/registry/ui/Button';
import { ApiKey } from 'src/features/apiKeys/utils/types';
import { useCreateApiKeys } from 'src/features/apiKeys/api/useCreateApiKeys';
import { useParking } from 'src/providers/ParkingProvider';
import { LIST_API_KEYS_RESOURCE } from 'src/features/apiKeys/api/useListApiKeys';
import { apiKeysMessages } from 'src/features/apiKeys/utils/apiKeysMessages';
import ApiKeyFormModal from 'src/features/apiKeys/components/forms/ApiKeyFormModal';
import { paymentsMessages } from 'src/features/payment/utils/messages';

interface ApiKeyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedApiKey?: ApiKey;
    children?: React.ReactNode;
}

function CreateApiKeyFormModal<TData>({ className }: ApiKeyFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const [apiKey, setApiKey] = React.useState<string | null>(null);

    const dialogTitle = `${formatMessage(generalMessages.createEntry, { entry: formatMessage(apiKeysMessages.apiKey) })}`;

    const { mutate, isPending } = useCreateApiKeys({
        onSuccess: (success) => {
            setApiKey(success.api_key);
            void queryClient.invalidateQueries({ queryKey: [LIST_API_KEYS_RESOURCE] });
            toast({
                title: formatMessage(apiKeysMessages.createApiKeySuccess),
                description: formatMessage(apiKeysMessages.createApiKeySuccessDescription),
            });
        },
    });

    return (
        <>
            <ApiKeyFormModal
                className={className}
                mutate={(values, onSuccess, onError) => mutate({ parkingId, ...values }, { onSuccess, onError })}
                title={dialogTitle}
                icon={ICON_MAP.ADD}
                isPending={isPending}
                subtitle={formatMessage(apiKeysMessages.addApiKeySubtitle)}
            />
            <Dialog open={!!apiKey} onOpenChange={() => setApiKey(null)}>
                <DialogContent>
                    <DialogHeader>
                        <div className="flex gap-2">
                            <SquareIcon icon={ICON_MAP.DANGER} />
                            <div>
                                <DialogTitle>{formatMessage(paymentsMessages.copyApiKey)}</DialogTitle>
                                <DialogDescription>{formatMessage(apiKeysMessages.copyNewApiKey)}</DialogDescription>
                            </div>{' '}
                        </div>
                    </DialogHeader>
                    <DialogSeparator />
                    <DialogBody>
                        <p className="text-gray-500">
                            {formatMessage(paymentsMessages.copyGeneratedApiKey, {
                                key: apiKey,
                                b: (chunks: React.ReactNode[]) => <b className="font-semibold">{chunks}</b>,
                            })}
                        </p>
                    </DialogBody>
                    <DialogSeparator />
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" className="flex-1">
                                {formatMessage(generalMessages.close)}
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default CreateApiKeyFormModal;
